import { APIService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";
import { IEventTravelBlockParams } from "Bands/types";

interface IJSONAPITravelBlock {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPITravelBlockCollection {
  [IDataKeys.eventTravelBlock]: IJSONAPITravelBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export interface IJSONAPISuggestedLocationCollection {
  [IDataKeys.eventSuggestedLocation]: IJSONAPITravelBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class EventTravelBlockService {
  static getBlocks = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPITravelBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const blocks: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCKS,
          data: queryParams,
        });
        resolve(blocks as IJSONAPITravelBlockCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_TRAVEL_BLOCKS);
        return reject(error);
      }
    });

  static getBlock = (id: string): Promise<IJSONAPITravelBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_TRAVEL_BLOCK);
        return reject(error);
      }
    });

  static createBlock = (data: IEventTravelBlockParams): Promise<IJSONAPITravelBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCKS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_TRAVEL_BLOCK);
        return reject(error);
      }
    });

  static editBlockTitle = ({
    id,
    title,
  }: {
    id: string;
    title: string;
  }): Promise<IJSONAPITravelBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK(id),
          data: { title },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_TRAVEL_BLOCK_TITLE);
        return reject(error);
      }
    });

  static editBlock = ({
    id,
    data,
  }: {
    id: string;
    data: IEventTravelBlockParams;
  }): Promise<IJSONAPITravelBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_TRAVEL_BLOCK);
        return reject(error);
      }
    });

  static deleteBlock = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_TRAVEL_BLOCK);
        return reject(error);
      }
    });

  static getSuggestedLocations = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPISuggestedLocationCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const locations: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_SUGGESTED_LOCATIONS,
          data: queryParams,
        });
        resolve(locations as IJSONAPISuggestedLocationCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_SUGGESTED_LOCATIONS);
        return reject(error);
      }
    });

  static getSuggestedLocation = (id: string): Promise<IJSONAPISuggestedLocationCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const location: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_SUGGESTED_LOCATION(id),
        });
        resolve(location as IJSONAPISuggestedLocationCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_SUGGESTED_LOCATION);
        return reject(error);
      }
    });
}
